import { FC, useRef, useEffect, useState } from 'react';
import { Modal, Text, LoadingOverlay } from '@mantine/core';
import { Patient } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { usePatientSubmit } from '@hooks/usePatientSubmit';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { PatientEditDemographicsForm } from '../../../../components/forms/PatientEditDemographicsForm';
import { usePatientDemographicsForm } from '@hooks/usePatientDemographicsForm';

interface PatientEditDemographicsModalProps {
  opened: boolean;
  onClose: () => void;
  patient: Patient | null;
  onPatientSaved?: (patient: Patient) => void;
}

/**
 * Self-contained modal component for editing patient demographics
 * Handles all form state, initialization, validation, and submission internally
 *
 * @param props - Component props
 * @param props.opened - Whether the modal is open
 * @param props.onClose - Callback to close the modal
 * @param props.patient - Patient to edit, or null for new patient
 * @param props.onPatientSaved - Callback when patient is saved successfully
 * @returns JSX.Element
 */
export const PatientEditDemographicsModal: FC<
  PatientEditDemographicsModalProps
> = ({ opened, onClose, patient, onPatientSaved }) => {
  const { t } = useTranslation(['create-patient-modal']);

  const formActionsRef = useRef<FormActionButtonsRef>(null);

  // Use the form hook - handles configuration, validation, initialization, and baseline tracking
  const {
    form,
    requiredFields,
    relatedPersons,
    hasChanges,
    handleDiscardChanges,
    resetForm,
    refreshRelatedPeople,
  } = usePatientDemographicsForm({
    patient,
    opened,
  });

  // Handle close
  const handleClose = () => {
    onClose();
    form.resetDirty();
  };

  // Patient submit hook
  const { onSubmit, loading } = usePatientSubmit({
    isNewPatient: !patient,
    onPatientSaved: (updatedPatient) => {
      // Refresh related people after save
      refreshRelatedPeople();
      onPatientSaved?.(updatedPatient);
      handleClose();
    },
  });

  // Track previous opened state
  const [prevOpened, setPrevOpened] = useState(opened);

  // Reset form when modal closes (only on transition from open to closed)
  useEffect(() => {
    if (prevOpened && !opened) {
      // Modal just closed, reset the form
      resetForm();
    }
    setPrevOpened(opened);
  }, [opened, prevOpened, resetForm]);


  // Handle form submission
  const handleFormSubmit = (values: typeof form.values) => {
    onSubmit(values, patient, relatedPersons);
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      size={700}
      title={
        <Text fw={700} size="lg">
          {patient
            ? t('labels.editTitle', { defaultValue: 'Edit Patient' })
            : t('labels.createTitle', { defaultValue: 'Create Patient' })}
        </Text>
      }
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <LoadingOverlay visible={loading} />
      <form
        onSubmit={form.onSubmit(handleFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
      >
        <PatientEditDemographicsForm
          form={form}
          requiredFields={requiredFields}
        />

        <FormActionButtons
          ref={formActionsRef}
          onDiscard={handleDiscardChanges}
          onClose={handleClose}
          hasUnsavedChanges={hasChanges}
          isLoading={loading}
          saveLabel={t('labels.saveButton', { defaultValue: 'Save' })}
        />
      </form>
    </Modal>
  );
};
